import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trash2, RotateCcw, Package, Users, ClipboardList, Layers, Inbox, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "all", label: "Semua", icon: Trash2 },
  { v: "product", label: "Produk", icon: Package },
  { v: "variant", label: "Varian", icon: Layers },
  { v: "rental", label: "Transaksi", icon: ClipboardList },
  { v: "pending_booking", label: "Booking Web", icon: Inbox },
  { v: "customer", label: "Pelanggan", icon: Users },
];
const KIND_LABEL = Object.fromEntries(KINDS.map(k => [k.v, k.label]));

function daysLeft(deletedAt) {
  const d = new Date(deletedAt); d.setDate(d.getDate() + 30);
  return Math.max(0, Math.ceil((d - new Date()) / 86400000));
}

function TrashRow({ t, onRestore, onPurge }) {
  const img = t.data?.image_url;
  const left = daysLeft(t.deleted_at);
  return (
    <div className="border border-neutral-200 rounded-md p-3 flex items-center gap-3 hover:bg-neutral-50" data-testid={`trash-row-${t.id}`}>
      <div className="w-12 h-12 rounded bg-neutral-100 shrink-0 overflow-hidden flex items-center justify-center">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Trash2 className="w-5 h-5 text-neutral-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{KIND_LABEL[t.kind] || t.kind}</span>
          <div className="font-medium text-sm truncate">{t.label}</div>
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">
          Dihapus {new Date(t.deleted_at).toLocaleString("id-ID")} · <span className={left <= 5 ? "text-red-600 font-semibold" : ""}>hapus permanen dalam {left} hari</span>
          {t.kind === "variant" && t.extra?.product_name && <> · Produk: {t.extra.product_name}</>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={() => onRestore(t)} data-testid={`restore-${t.id}`}><RotateCcw className="w-3 h-3 mr-1" /> Pulihkan</Button>
        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onPurge(t)} data-testid={`purge-${t.id}`}><Trash2 className="w-3 h-3 mr-1" /> Permanen</Button>
      </div>
    </div>
  );
}

export default function Trash() {
  const [items, setItems] = useCached("trash", []);
  const load = () => api.get("/trash").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    try { await api.post(`/trash/${t.id}/restore`); toast.success("Data dipulihkan"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal memulihkan"); }
  };
  const purge = async (t) => {
    if (!window.confirm(`Hapus permanen "${t.label}"? Tidak dapat dikembalikan.`)) return;
    try { await api.delete(`/trash/${t.id}`); toast.success("Dihapus permanen"); load(); }
    catch { toast.error("Gagal"); }
  };
  const purgeAll = async () => {
    if (!window.confirm("Kosongkan seluruh sampah secara permanen?")) return;
    try { await api.delete("/trash"); toast.success("Sampah dikosongkan"); load(); }
    catch { toast.error("Gagal"); }
  };

  const counts = useMemo(() => items.reduce((a, t) => ({ ...a, [t.kind]: (a[t.kind] || 0) + 1 }), {}), [items]);

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Keamanan Data</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Sampah / Trash</h1>
          <p className="text-sm text-neutral-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Data di sini otomatis dihapus permanen setelah 30 hari.</p>
        </div>
        {items.length > 0 && <Button variant="outline" className="text-red-600" onClick={purgeAll} data-testid="purge-all-btn"><Trash2 className="w-4 h-4 mr-1" /> Kosongkan Sampah</Button>}
      </div>

      <Tabs defaultValue="all">
        <TabsList className="w-full md:w-fit overflow-x-auto flex">
          {KINDS.map(k => (
            <TabsTrigger key={k.v} value={k.v} data-testid={`trash-tab-${k.v}`}>
              <k.icon className="w-4 h-4 mr-1" /> {k.label} ({k.v === "all" ? items.length : counts[k.v] || 0})
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map(k => {
          const data = k.v === "all" ? items : items.filter(t => t.kind === k.v);
          return (
            <TabsContent key={k.v} value={k.v} className="mt-4">
              <Card className="border-neutral-200 shadow-none">
                <CardContent className="p-4 space-y-2">
                  {data.map(t => <TrashRow key={t.id} t={t} onRestore={restore} onPurge={purge} />)}
                  {data.length === 0 && <div className="text-center py-12 text-neutral-500">Sampah kosong</div>}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
